<template>
  <header class="education-topbar">
    <div class="breadcrumb" aria-label="面包屑">
      <span>AI 教育</span>
      <Icon icon="ep:arrow-right" :size="14" />
      <strong>{{ pageTitle }}</strong>
    </div>

    <div class="topbar-actions">
      <el-select
        v-if="organizationOptions.length > 1"
        :model-value="membershipId"
        class="org-switch"
        placeholder="切换身份"
        filterable
        @change="onSwitchOrg"
      >
        <el-option
          v-for="org in organizationOptions"
          :key="org.membershipId"
          :label="orgLabel(org)"
          :value="org.membershipId!"
        />
      </el-select>
      <el-tag v-else-if="currentOrgLabel" size="small" type="info" effect="plain">
        {{ currentOrgLabel }}
      </el-tag>
      <el-input class="topbar-search" placeholder="搜索课程、班级或学生" clearable>
        <template #prefix><Icon icon="ep:search" /></template>
      </el-input>
      <el-button circle aria-label="教学日历"><Icon icon="ep:calendar" /></el-button>
      <el-button circle aria-label="通知消息"><Icon icon="ep:bell" /></el-button>
      <el-button type="primary" @click="$emit('toggle-assistant')">
        <Icon icon="ep:magic-stick" />
        {{ assistantCollapsed ? '打开 AI 助手' : '收起 AI 助手' }}
      </el-button>
    </div>
  </header>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { EducationOrganizationOptionVO } from '@/api/aiEducation'
import { useEducationWorkspace } from '../composables/useEducationWorkspace'
import { AI_EDUCATION_BASE, ROLE_WORKBENCHES } from '../composables/useAdminEducationMenu'

defineOptions({ name: 'AiEducationTopbar' })

defineProps<{ assistantCollapsed: boolean }>()
defineEmits<{ 'toggle-assistant': [] }>()

const route = useRoute()
const router = useRouter()
const message = useMessage()
const { workspace, organizations, membershipId, initialize, switchOrganization } =
  useEducationWorkspace()

const pageTitle = computed(() => String(route.meta.title || '工作台'))
const organizationOptions = computed(() =>
  organizations.value.filter((item) => item.membershipId != null)
)

const WORKSPACE_LABEL: Record<string, string> = {
  teacher: '教师',
  student: '学生',
  parent: '家长',
  principal: '校长',
  researcher: '教研员',
  regionAdmin: '区域管理员',
  platformAdmin: '平台管理员'
}

const orgLabel = (org: EducationOrganizationOptionVO) => {
  const role = WORKSPACE_LABEL[org.workspace] || org.workspace
  return `${org.organizationName} · ${role}`
}

const currentOrgLabel = computed(() => {
  if (!workspace.value) return ''
  const role = WORKSPACE_LABEL[workspace.value.workspace] || workspace.value.workspace
  return `${workspace.value.organizationName} · ${role}`
})

const homePathOf = (ws: string) => {
  const hit = ROLE_WORKBENCHES.find((item) => item.workspace === ws)
  return hit?.path || `${AI_EDUCATION_BASE}/overview`
}

const onSwitchOrg = async (nextMembershipId: number) => {
  try {
    const next = await switchOrganization(nextMembershipId)
    const role = WORKSPACE_LABEL[next.workspace] || next.workspace
    message.success(`已切换身份：${next.organizationName} · ${role}`)
    await router.push(homePathOf(next.workspace))
  } catch (e: any) {
    message.error(e?.message || '切换身份失败')
  }
}

onMounted(() => {
  initialize().catch(() => undefined)
})
</script>

<style scoped>
.education-topbar {
  display: flex;
  min-width: 0;
  height: 56px;
  flex: 0 0 56px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 0 20px;
  border-bottom: 1px solid #e4e7ed;
  background: #fff;
}

.breadcrumb,
.topbar-actions {
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 10px;
}

.breadcrumb {
  color: #697681;
  white-space: nowrap;
}

.breadcrumb strong {
  overflow: hidden;
  color: #17222d;
  text-overflow: ellipsis;
}

.org-switch {
  width: 220px;
}

.topbar-search {
  width: min(240px, 22vw);
}

@media (max-width: 1023px) {
  .topbar-search {
    display: none;
  }
}

@media (max-width: 767px) {
  .education-topbar {
    padding: 0 12px;
  }

  .org-switch {
    width: 140px;
  }

  .topbar-actions :deep(.el-button span) {
    font-size: 0;
  }
}
</style>
